import React, { useEffect, useMemo, useState } from "react";
import { api, errFrom, absUrl } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import { Progress } from "../../components/ui/progress";
import { BrandMark } from "../../components/AppShell";
import { toast } from "sonner";
import { Check, ChevronLeft, ChevronRight, FileText, Upload, Award, UserRound, ShieldCheck, BookOpen } from "lucide-react";

const STEPS = [
  { key: "welcome", label: "Welcome", icon: UserRound },
  { key: "profile", label: "Your details", icon: UserRound },
  { key: "id_proof", label: "ID proof", icon: Upload },
  { key: "policy", label: "Policy", icon: ShieldCheck },
  { key: "sops", label: "Read SOPs", icon: BookOpen },
  { key: "assessment", label: "Assessment", icon: Award },
];

export default function Onboarding() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState(null);
  const [step, setStep] = useState(0);
  const [sops, setSops] = useState([]);
  const [policy, setPolicy] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // Profile
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [emergencyName, setEmergencyName] = useState("");
  const [emergencyPhone, setEmergencyPhone] = useState("");

  // Policy
  const [accepted, setAccepted] = useState(false);
  const [typedName, setTypedName] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/employee/onboarding/status");
        setStatus(data);
        if (data.assessment_passed) { nav("/employee", { replace: true }); return; }
        const stageIdx = {
          "set_password": 0, "profile": 1, "id_proof": 2, "policy": 3, "sops": 4, "assessment": 5, "complete": 5,
        }[data.stage || "profile"] ?? 1;
        setStep(stageIdx);
      } catch (e) { toast.error(errFrom(e)); }
    })();
  }, [nav]);

  useEffect(() => {
    if (step >= 3 && !policy) {
      api.get("/employee/sops").then(({ data }) => { setSops(data.sops); setPolicy(data.policy); });
    }
    if (step === 5 && questions.length === 0) {
      api.get("/employee/assessment/questions").then(({ data }) => setQuestions(data.questions));
    }
  }, [step, policy, questions.length]);

  const progress = useMemo(() => ((step) / (STEPS.length - 1)) * 100, [step]);

  async function saveProfile() {
    if (!name.trim() || !phone.trim()) return toast.error("Name and phone are required");
    setLoading(true);
    try {
      await api.post("/employee/onboarding/profile", { name, phone, emergency_contact_name: emergencyName, emergency_contact_phone: emergencyPhone });
      await refresh();
      setStep(2);
    } catch (e) { toast.error(errFrom(e)); }
    finally { setLoading(false); }
  }

  async function uploadId(file) {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/employee/onboarding/id-proof", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("ID uploaded");
      setStep(3);
    } catch (e) { toast.error(errFrom(e)); }
    finally { setLoading(false); }
  }

  async function signPolicy() {
    if (!accepted) return toast.error("You must accept the policy to continue");
    if ((typedName || "").trim().toLowerCase() !== (user?.name || "").trim().toLowerCase()) return toast.error("Typed name must match your full name exactly");
    setLoading(true);
    try {
      await api.post("/employee/onboarding/policy-sign", { accepted: true, typed_name: typedName });
      toast.success("Policy signed");
      setStep(4);
    } catch (e) { toast.error(errFrom(e)); }
    finally { setLoading(false); }
  }

  async function sopsDone() {
    setLoading(true);
    try {
      await api.post("/employee/onboarding/sops-complete");
      toast.success("Great! Ready for the assessment.");
      setStep(5);
    } catch (e) { toast.error(errFrom(e)); }
    finally { setLoading(false); }
  }

  async function submitAssessment() {
    if (Object.keys(answers).length !== questions.length) return toast.error("Please answer every question");
    const payload = questions.map((q) => answers[q.order]);
    setLoading(true);
    try {
      const { data } = await api.post("/employee/assessment/submit", { answers: payload });
      setResult(data);
      await refresh();
      if (data.passed) {
        toast.success("You passed! Welcome to the team.");
      } else {
        toast.error(`Score ${data.score}%. You need 100%. Please re-read the SOPs and try again.`);
      }
    } catch (e) { toast.error(errFrom(e)); }
    finally { setLoading(false); }
  }

  function retakeQuiz() {
    setResult(null);
    setAnswers({});
    setStep(4); // back to SOPs
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] relative">
      <div className="absolute inset-0 grid-texture opacity-30 pointer-events-none" />
      <header className="relative border-b border-white/5 px-6 py-5 flex items-center justify-between">
        <BrandMark size={34} />
        <div className="text-overline">Onboarding · {STEPS[step]?.label}</div>
      </header>

      <div className="relative max-w-3xl mx-auto px-4 md:px-6 py-8">
        {/* Step rail */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            {STEPS.map((s, i) => (
              <div key={s.key} className="flex items-center gap-2 flex-1">
                <div className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-[#FF3B30]" : "bg-white/10"}`} />
              </div>
            ))}
          </div>
          <div className="flex justify-between text-[9px] sm:text-[10px] text-zinc-500 uppercase tracking-wider gap-1 flex-wrap sm:flex-nowrap">
            {STEPS.map((s, i) => (
              <span key={s.key} className={`${i === step ? "text-[#FF3B30] font-semibold" : ""} truncate`}>{s.label}</span>
            ))}
          </div>
        </div>

        {/* Welcome */}
        {step === 0 && (
          <Card>
            <h1 className="font-heading text-4xl font-bold tracking-tight">Welcome to Dapper Detailing.</h1>
            <p className="text-zinc-400 mt-4 leading-relaxed">
              Over the next few steps, you'll provide your details, upload an ID, sign the company policy, read through our 4 SOPs, and complete a short assessment.
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mt-6">
              <Bullet icon={<BookOpen size={18} />}>Read the 4 SOPs thoroughly</Bullet>
              <Bullet icon={<Award size={18} />}>Pass a 20-question assessment (100% required)</Bullet>
              <Bullet icon={<ShieldCheck size={18} />}>Sign the onboarding policy</Bullet>
              <Bullet icon={<FileText size={18} />}>Upload a government ID</Bullet>
            </div>
            <div className="mt-8 flex justify-end">
              <Button onClick={() => setStep(1)} className="bg-[#FF3B30] hover:bg-[#E02920] h-11 px-6" data-testid="onboarding-start-button">Let's begin <ChevronRight size={16} className="ml-1" /></Button>
            </div>
          </Card>
        )}

        {/* Profile */}
        {step === 1 && (
          <Card>
            <StepTitle icon={UserRound} title="Your details" hint="We use these to set up your account and emergency contact." />
            <div className="grid sm:grid-cols-2 gap-4 mt-6">
              <Field label="Full name"><Input value={name} onChange={(e) => setName(e.target.value)} className="bg-[#0A0A0A] border-white/10 h-11" data-testid="onboarding-name-input" /></Field>
              <Field label="Phone"><Input value={phone} onChange={(e) => setPhone(e.target.value)} className="bg-[#0A0A0A] border-white/10 h-11" data-testid="onboarding-phone-input" /></Field>
              <Field label="Email"><Input value={user?.email || ""} disabled className="bg-[#0A0A0A] border-white/10 h-11 opacity-70" /></Field>
              <Field label="Emergency contact name"><Input value={emergencyName} onChange={(e) => setEmergencyName(e.target.value)} className="bg-[#0A0A0A] border-white/10 h-11" data-testid="onboarding-emergency-name-input" /></Field>
              <Field label="Emergency contact phone"><Input value={emergencyPhone} onChange={(e) => setEmergencyPhone(e.target.value)} className="bg-[#0A0A0A] border-white/10 h-11" data-testid="onboarding-emergency-phone-input" /></Field>
            </div>
            <StepNav onPrev={() => setStep(0)} onNext={saveProfile} nextLabel="Continue" loading={loading} nextTestId="onboarding-profile-next" />
          </Card>
        )}

        {/* ID Proof */}
        {step === 2 && (
          <Card>
            <StepTitle icon={Upload} title="Upload your ID proof" hint="A clear photo or PDF of a government-issued ID (driver's license, passport, etc.). Max 15MB." />
            <label className="mt-6 block border-2 border-dashed border-white/15 rounded-xl p-10 text-center cursor-pointer hover:border-[#FF3B30]/50 transition-colors" data-testid="onboarding-id-upload-dropzone">
              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => uploadId(e.target.files?.[0])} data-testid="onboarding-id-file-input" />
              <Upload className="mx-auto text-[#FF3B30]" size={28} />
              <div className="mt-3 font-heading text-lg">Click to select a file</div>
              <div className="text-zinc-500 text-xs mt-1">PDF, JPG, PNG, or WebP</div>
            </label>
            <StepNav onPrev={() => setStep(1)} onNext={() => setStep(3)} nextLabel="Skip to next" disabled={loading} />
          </Card>
        )}

        {/* Policy */}
        {step === 3 && (
          <Card>
            <StepTitle icon={ShieldCheck} title="Sign the onboarding policy" hint="Read the full policy below, acknowledge, and type your full name to sign." />
            {policy?.url ? (
              <iframe
                src={absUrl(policy.url)}
                title="Policy PDF"
                className="w-full h-[520px] rounded-lg border border-white/10 bg-black mt-4"
                data-testid="policy-pdf-frame"
              />
            ) : (
              <div className="mt-4 h-[120px] rounded-lg border border-white/10 bg-[#0A0A0A] flex items-center justify-center text-sm text-zinc-500">Loading policy…</div>
            )}
            {policy?.url && (
              <a href={absUrl(policy.url)} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-2 text-xs text-[#FF3B30] hover:text-[#E02920]" data-testid="policy-open-new-tab">
                <FileText size={14} />Open in new tab
              </a>
            )}
            <div className="mt-6 space-y-4">
              <div className={`flex items-start gap-3 bg-[#0A0A0A] border rounded-lg p-4 transition-colors ${accepted ? "border-[#FF3B30]/40" : "border-white/10"}`}>
                <Checkbox id="acc" checked={accepted} onCheckedChange={(v) => setAccepted(!!v)} className="mt-1 border-white/30 data-[state=checked]:bg-[#FF3B30] data-[state=checked]:border-[#FF3B30]" data-testid="policy-accept-checkbox" />
                <label htmlFor="acc" className="text-sm text-zinc-300 leading-relaxed cursor-pointer">
                  I confirm I have <b className="text-white">read the entire Employee Onboarding Policy &amp; Procedure</b> above. I understand I must follow SOPs strictly, maintain professionalism, and comply with all safety guidelines.
                </label>
              </div>
              <Field label="Type your full name as signature">
                <Input value={typedName} onChange={(e) => setTypedName(e.target.value)} placeholder={user?.name || ""} disabled={!accepted} className="bg-[#0A0A0A] border-white/10 h-11 font-heading disabled:opacity-50" data-testid="policy-typed-name-input" />
                <div className="text-[11px] text-zinc-500 mt-2">{accepted ? "Must match your full name exactly." : "Tick the acknowledgement above to enable signature."}</div>
              </Field>
            </div>
            <StepNav onPrev={() => setStep(2)} onNext={signPolicy} nextLabel="Sign &amp; continue" loading={loading} disabled={!accepted || !typedName.trim()} nextTestId="policy-sign-button" />
          </Card>
        )}

        {/* SOPs */}
        {step === 4 && (
          <Card>
            <StepTitle icon={BookOpen} title="Read every SOP carefully" hint="Your 20-question assessment will test you on these. 100% is required to pass." />
            <div className="mt-6 space-y-3">
              {sops.map((s) => (
                <a key={s.code} href={absUrl(s.url)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 brand-surface-2 p-4 rounded-lg hover-lift" data-testid={`sop-open-${s.code}`}>
                  <div className="w-10 h-10 bg-[#FF3B30]/15 text-[#FF3B30] rounded-lg flex items-center justify-center"><FileText size={18} /></div>
                  <div className="flex-1">
                    <div className="font-heading font-semibold text-white">{s.title}</div>
                    <div className="text-[11px] text-zinc-500 uppercase tracking-wider">Open PDF</div>
                  </div>
                  <ChevronRight size={16} className="text-zinc-500" />
                </a>
              ))}
            </div>
            <div className="mt-6 text-sm text-zinc-400 bg-[#FF3B30]/8 border border-[#FF3B30]/30 rounded-lg p-4">
              <b className="text-white">Heads up:</b> After this, you'll take a 20-question assessment. Every question must be correct to pass. You can retake it if needed.
            </div>
            <StepNav onPrev={() => setStep(3)} onNext={sopsDone} nextLabel="I'm ready for the assessment" loading={loading} nextTestId="sops-complete-button" />
          </Card>
        )}

        {/* Assessment */}
        {step === 5 && (
          <Card>
            {!result ? (
              <>
                <StepTitle icon={Award} title="Assessment" hint={`Answer all ${questions.length} questions. 100% required to pass.`} />
                <div className="mt-3 mb-6"><Progress value={(Object.keys(answers).length / Math.max(questions.length, 1)) * 100} className="h-1.5 bg-white/10" /></div>
                <div className="space-y-6">
                  {questions.map((q, idx) => (
                    <div key={q.order} className="brand-surface-2 rounded-lg p-5" data-testid={`question-${q.order}`}>
                      <div className="text-[10px] uppercase tracking-widest text-[#FF3B30] font-semibold">{q.section}</div>
                      <div className="font-heading font-semibold mt-1 mb-4">Q{idx + 1}. {q.q}</div>
                      <div className="grid gap-2">
                        {Object.entries(q.options).map(([k, v]) => {
                          const selected = answers[q.order] === k;
                          return (
                            <label key={k} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${selected ? "border-[#FF3B30] bg-[#FF3B30]/5" : "border-white/10 hover:border-white/20"}`} data-testid={`question-${q.order}-option-${k}`}>
                              <input type="radio" name={`q-${q.order}`} className="accent-[#FF3B30] mt-1" checked={selected} onChange={() => setAnswers({ ...answers, [q.order]: k })} />
                              <span className="text-sm text-zinc-200"><span className="text-[#FF3B30] font-semibold mr-2">{k}.</span>{v}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-6 flex justify-end">
                  <Button onClick={submitAssessment} disabled={loading} className="bg-[#FF3B30] hover:bg-[#E02920] h-11 px-8" data-testid="assessment-submit-button">
                    {loading ? "Grading…" : "Submit assessment"}
                  </Button>
                </div>
              </>
            ) : result.passed ? (
              <div className="text-center py-10">
                <div className="w-20 h-20 mx-auto rounded-full bg-[#FF3B30]/15 text-[#FF3B30] flex items-center justify-center"><Check size={40} /></div>
                <h2 className="font-heading text-3xl font-bold mt-5">You passed! 🎉</h2>
                <p className="text-zinc-400 mt-2">Score: {result.score}%. Welcome to the Dapper Detailing team.</p>
                <Button onClick={() => nav("/employee", { replace: true })} className="mt-6 bg-[#FF3B30] hover:bg-[#E02920] h-11 px-6" data-testid="assessment-enter-dashboard-button">
                  Enter dashboard <ChevronRight size={16} className="ml-1" />
                </Button>
              </div>
            ) : (
              <div className="text-center py-10">
                <div className="w-20 h-20 mx-auto rounded-full bg-red-500/15 text-red-500 flex items-center justify-center text-3xl font-heading font-bold">!</div>
                <h2 className="font-heading text-3xl font-bold mt-5">Not quite there.</h2>
                <p className="text-zinc-400 mt-2">Score: {result.score}%. You need 100% to pass. Head back to the SOPs and try again.</p>
                <Button onClick={retakeQuiz} className="mt-6 bg-[#FF3B30] hover:bg-[#E02920] h-11 px-6" data-testid="assessment-retake-button">
                  Re-read SOPs &amp; retake
                </Button>
              </div>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}

function Card({ children }) {
  return <div className="brand-surface rounded-2xl p-6 md:p-8">{children}</div>;
}
function Field({ label, children }) {
  return <div><Label className="text-zinc-400 text-xs uppercase tracking-wider">{label}</Label><div className="mt-2">{children}</div></div>;
}
function Bullet({ icon, children }) {
  return <div className="flex items-start gap-3 brand-surface-2 rounded-lg p-4"><div className="text-[#FF3B30] mt-0.5">{icon}</div><div className="text-sm text-zinc-300">{children}</div></div>;
}
function StepTitle({ icon: Icon, title, hint }) {
  return <div className="flex items-start gap-4"><div className="w-11 h-11 rounded-lg bg-[#FF3B30]/15 text-[#FF3B30] flex items-center justify-center"><Icon size={20} /></div><div><h2 className="font-heading text-2xl font-bold tracking-tight">{title}</h2>{hint && <p className="text-zinc-500 text-sm mt-1">{hint}</p>}</div></div>;
}
function StepNav({ onPrev, onNext, nextLabel, loading, disabled, nextTestId }) {
  return (
    <div className="mt-8 flex justify-between">
      <Button variant="ghost" onClick={onPrev} className="text-zinc-400 hover:text-white" data-testid="step-prev"><ChevronLeft size={16} className="mr-1" />Back</Button>
      <Button onClick={onNext} disabled={loading || disabled} className="bg-[#FF3B30] hover:bg-[#E02920] h-11 px-6" data-testid={nextTestId || "step-next"}>
        {loading ? "Saving…" : nextLabel} <ChevronRight size={16} className="ml-1" />
      </Button>
    </div>
  );
}
